import { useSelector as _useSelector, TypedUseSelectorHook } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';

import { PrerenderUser } from '../../features/PrerenderUser';

export const useSelector: TypedUseSelectorHook<{
  prerenderUser: PrerenderUser;
}> = _useSelector;

export const PRERENDER_EMAIL = '@prerender.io';

const selectRenderCounter = (state: { prerenderUser: PrerenderUser }) => state.prerenderUser.renderCounter;
const selectPlanLimit = (state: { prerenderUser: PrerenderUser }) => state.prerenderUser.plan.limit;

const selectRenderUsagePercentage = createSelector(
  [selectRenderCounter, selectPlanLimit],
  (currentRenderCount, planLimit) => {
    return +((currentRenderCount / planLimit) * 100).toFixed(2);
  }
);

export const usePrerenderUser = () => useSelector((state) => state.prerenderUser);

export const usePrerenderUserId = () => useSelector((state) => state.prerenderUser.id);
export const usePrerenderEmail = () => useSelector((state) => state.prerenderUser.email);
export const usePrerenderToken = () => useSelector((state) => state.prerenderUser.token);
export const usePrerenderUserFeatures = () => useSelector((state) => state.prerenderUser.features);
export const useIsAccountOwner = () =>
  useSelector((state) => {
    const { email, adminEmail } = state.prerenderUser;
    return !!email && !!adminEmail && email.toLowerCase() === adminEmail.toLowerCase();
  });
export const useCharbeePlanId = () => useSelector((state) => state.prerenderUser.chargebeePlanId);
export const useCurrentPlan = () => useSelector((state) => state.prerenderUser.plan.current);
export const useHasTrackingCode = () => useSelector((state) => state.prerenderUser.trackingCodeInstalled);
export const useHasCompletedSurvey = () => useSelector((state) => state.prerenderUser.onboardingSurveyCompleted);
export const useCurrentRenderCount = () => useSelector((state) => state.prerenderUser.renderCounter);
export const useRenderUsagePercentage = () => useSelector(selectRenderUsagePercentage);
export type SubscriptionCancellationState = {
  cancelledAt: Date | null;
  recachingDisabled: boolean;
};
// Subscription is cancelled if the cancellation date is not in the future and recaching is disabled
export const isSubscriptionCancelled = (
  { cancelledAt, recachingDisabled }: SubscriptionCancellationState,
  now: Date = new Date()
): boolean => {
  if (cancelledAt === null || !recachingDisabled) return false;

  return cancelledAt <= now;
};
export const useIsSubscriptionCancelled = () => useSelector((state) => isSubscriptionCancelled(state.prerenderUser));
export const useIsCancellationScheduled = () =>
  useSelector((state) => {
    const { cancelledAt } = state.prerenderUser;

    return !!cancelledAt && cancelledAt > new Date();
  });

export const useIsPrerenderUser = () => {
  const email = usePrerenderEmail();
  return email.endsWith(PRERENDER_EMAIL);
};

export const useUserRole = () => useSelector((state) => state.prerenderUser.role);
